import { useEffect } from 'react';
import { Input, Label } from '@kmgestao/ui';

import { useCepLookup } from '../../../leads/cep-lookup.js';
import { formatCep, normalizeCep, toTitleCase } from '../../../leads/formatters.js';
import type { FormField } from '../../schemas.js';

export interface CepFieldProps {
  field: FormField;
  value: string;
  onChange: (next: string) => void;
  /** Callback com street/district/city/uf quando BrasilAPI v2
   * responde. Renderer decide se aplica (respeitando valores
   * editados manualmente pelo lead). */
  onAutofill?: (data: {
    street?: string | undefined;
    district?: string | undefined;
    city?: string | undefined;
    uf?: string | undefined;
  }) => void;
  onBlur?: (() => void) | undefined;
  error?: string | undefined;
  disabled?: boolean | undefined;
  idPrefix?: string | undefined;
}

/**
 * Campo de CEP com máscara progressiva `XXXXX-XXX` e autofill
 * opcional via BrasilAPI v2 — mesmo padrão do `LeadFormFields`.
 */
export function CepField({
  field,
  value,
  onChange,
  onAutofill,
  onBlur,
  error,
  disabled,
  idPrefix = 'form',
}: CepFieldProps) {
  const inputId = `${idPrefix}-${field.id}`;
  const descId = field.description ? `${inputId}-desc` : undefined;
  const errorId = error ? `${inputId}-err` : undefined;

  const digits = normalizeCep(value);
  const autofillEnabled = field.autofill === 'brasilapi-cep';
  const cepQuery = useCepLookup(autofillEnabled ? digits : '');

  useEffect(() => {
    if (!cepQuery.data || !onAutofill) return;
    const d = cepQuery.data;
    onAutofill({
      street: d.street ? toTitleCase(d.street) : undefined,
      district: d.neighborhood ? toTitleCase(d.neighborhood) : undefined,
      city: d.city ? toTitleCase(d.city) : undefined,
      uf: d.state?.toUpperCase().slice(0, 2),
    });
  }, [cepQuery.data, onAutofill]);

  return (
    <div className="space-y-2">
      <Label htmlFor={inputId}>
        {field.label}
        {field.required ? <span className="ml-0.5 text-destructive"> *</span> : null}
      </Label>
      {field.description ? (
        <p id={descId} className="text-xs text-muted-foreground">
          {field.description}
        </p>
      ) : null}
      <Input
        id={inputId}
        type="text"
        value={value}
        onChange={(e) => onChange(formatCep(e.target.value))}
        onBlur={onBlur}
        placeholder={field.placeholder ?? '00000-000'}
        autoComplete={field.autocomplete ?? 'postal-code'}
        inputMode="numeric"
        maxLength={9}
        hasError={!!error}
        disabled={disabled}
        aria-describedby={[descId, errorId].filter(Boolean).join(' ') || undefined}
        aria-invalid={!!error}
      />
      {autofillEnabled && cepQuery.isLoading ? (
        <p className="text-xs text-muted-foreground">Consultando CEP…</p>
      ) : null}
      {autofillEnabled && cepQuery.isError ? (
        <p className="text-xs text-destructive" role="alert">
          {(cepQuery.error as Error).message}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
